import { EMPTY, formatDate, formatMoney, routes, type Support, SupportTypes } from '@pmai/contracts';
import {
  Button,
  Card,
  type Column,
  ConfirmDialog,
  DataTable,
  EmptyState,
  IconButton,
  PageHeader,
  StatusBadge,
  FilterSelect,
} from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useCustomerOptions } from '../../entities/customers/customer-options';
import { useApiQuery } from '../../shared/api/hooks';
import { Can, usePermissions } from '../../shared/auth/session';
import { useEnumLabel } from '../../shared/i18n';
import { useConfirm } from '../../shared/use-confirm';
import { SupportDialog } from './support-dialog';

const customerLink = (id: string, name: string) => (
  <Link to={`/customers/${id}`} className="font-medium text-brand-700 hover:underline">
    {name}
  </Link>
);

/** Support (FR-SUP-001…009). */
export function SupportPage() {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const permissions = usePermissions();
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Support | 'new' | null>(null);
  const customers = useCustomerOptions();
  const entries = useApiQuery(
    routes.support.list,
    { query: { customerId: customerId ?? undefined } },
    { placeholderData: (previous) => previous },
  );
  const gaps = useApiQuery(routes.support.gaps);
  const remove = useConfirm(routes.support.remove, {
    invalidates: [routes.support.list, routes.support.gaps],
    params: (s: Support) => ({ id: s.id }),
  });
  const f = (field: string) => t(`support.fields.${field}`);
  /** Dates, type and fee only for support in force (FR-SUP-001). */
  const active = (s: Support, value: string | null) => (s.isActive && value ? value : EMPTY);
  const yesNo = (value: boolean, warn = false) => (
    <StatusBadge
      tone={value ? (warn ? 'danger' : 'success') : 'neutral'}
      label={t(value ? 'common.yes' : 'common.no')}
    />
  );

  const columns: Column<Support>[] = [
    { key: 'customer', header: f('customer'), cell: (s) => customerLink(s.customerId, s.customerName) },
    { key: 'product', header: f('product'), cell: (s) => s.productName },
    { key: 'isActive', header: f('isActiveShort'), cell: (s) => yesNo(s.isActive) },
    {
      key: 'startDate',
      header: f('startDate'),
      cell: (s) => active(s, s.startDate && formatDate(s.startDate)),
    },
    { key: 'endDate', header: f('endDate'), cell: (s) => active(s, s.endDate && formatDate(s.endDate)) },
    {
      key: 'supportType',
      header: f('supportType'),
      cell: (s) => active(s, s.supportType && label(SupportTypes.byCode(s.supportType))),
    },
    ...(permissions.includes('finance.view')
      ? [
          {
            key: 'monthlyFee',
            header: f('monthlyFee'),
            align: 'right' as const,
            cell: (s: Support) => active(s, s.monthlyFee && formatMoney(s.monthlyFee)),
          },
        ]
      : []),
    { key: 'needsReview', header: f('needsReviewShort'), cell: (s) => yesNo(s.needsReview, true) },
  ];
  if (permissions.includes('support.edit'))
    columns.push({
      key: 'actions',
      header: t('common.actions'),
      align: 'right',
      cell: (s) => (
        <div className="flex justify-end gap-1">
          <IconButton
            icon="pencil"
            label={`${t('common.edit')}: ${s.customerName} — ${s.productName}`}
            onClick={() => setEditing(s)}
          />
          <IconButton
            icon="trash-2"
            label={`${t('common.delete')}: ${s.customerName} — ${s.productName}`}
            onClick={() => remove.ask(s)}
          />
        </div>
      ),
    });

  return (
    <>
      <PageHeader
        title={t('support.title')}
        actions={
          <Can perm="support.edit">
            <Button icon="plus" onClick={() => setEditing('new')}>
              {t('support.add')}
            </Button>
          </Can>
        }
      />
      <div className="flex flex-col gap-5">
        <div className="w-72">
          <FilterSelect
            label={f('customer')}
            allLabel={t('support.allCustomers')}
            options={customers}
            value={customerId}
            onChange={setCustomerId}
          />
        </div>
        <DataTable
          label={t('support.title')}
          rows={entries.data}
          rowKey={(s) => s.id}
          loading={entries.isPending}
          empty={<EmptyState icon="life-buoy" title={t('support.empty')} />}
          rowClassName={(s) => (s.isActive ? undefined : 'text-ink-muted bg-canvas/60')}
          columns={columns}
        />
        {!!gaps.data?.customersWithoutProducts.length && (
          <Card title={t('support.customersWithoutProducts')}>
            <ul aria-label={t('support.customersWithoutProducts')} className="flex flex-wrap gap-2">
              {gaps.data.customersWithoutProducts.map((c) => (
                <li key={c.id} className="rounded-full border border-border px-3 py-1 text-sm">
                  {customerLink(c.id, c.name)}
                </li>
              ))}
            </ul>
          </Card>
        )}
        {!!gaps.data?.productsWithoutSupport.length && (
          <Card title={t('support.productsWithoutSupport')}>
            <DataTable
              label={t('support.productsWithoutSupport')}
              rows={gaps.data.productsWithoutSupport}
              rowKey={(p) => p.productId}
              empty={null}
              columns={[
                {
                  key: 'customer',
                  header: f('customer'),
                  cell: (p) => customerLink(p.customerId, p.customerName),
                },
                { key: 'product', header: f('product'), cell: (p) => p.productName },
              ]}
            />
          </Card>
        )}
      </div>
      <SupportDialog
        open={editing !== null}
        entry={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        {...remove.dialog}
        title={t('support.deleteTitle')}
        message={t('support.deleteText', {
          name: remove.target ? `${remove.target.customerName} — ${remove.target.productName}` : '',
        })}
        confirmLabel={t('common.delete')}
      />
    </>
  );
}
